import { useState } from 'react';
import type { Startup } from '@shared/types';
import type { Pipeline } from '../../store/pipelineStore';
import { timeAgo } from '../../utils/format';
import { api } from '../../api/client';
import { FileDown, Sparkles, Loader, Zap, Check } from 'lucide-react';

const STAGE_LABELS: Record<string, string> = {
  idea: 'Idea', validation: 'Validation', mvp: 'MVP', growth: 'Growth',
};

const PIPELINE_STEPS = [
  { label: 'Profile', key: 'userProfile' as const },
  { label: 'Idea', key: 'idea' as const },
  { label: 'Reality Check', key: 'realityCheck' as const },
  { label: 'Positioning', key: 'positioning' as const },
  { label: 'Validation', key: 'validationPath' as const },
];

const GENERIC_CATEGORIES = ['marketplace', 'platform', 'software', 'app', 'tool', 'service', 'saas', 'application', 'solution'];

interface Props {
  startup: Startup;
  startupId: string;
  pipeline: Pipeline | null;
  isRunningAny: boolean;
  category?: string;
  onCategoryChange?: (category: string) => void;
  onRunIntelligence: () => void;
  onPrint: () => void;
}

export function StartupBriefCard({ startup, startupId, pipeline, isRunningAny, category, onCategoryChange, onRunIntelligence, onPrint }: Props) {
  const [editingCategory, setEditingCategory] = useState(false);
  const [categoryValue, setCategoryValue] = useState(category ?? '');
  const isGenericCategory = category && GENERIC_CATEGORIES.includes(category.toLowerCase().trim());

  const saveCategory = async (val: string) => {
    const trimmed = val.trim();
    if (trimmed === category) {
      setEditingCategory(false);
      return;
    }
    try {
      await api.put(`/startups/${startupId}`, { category: trimmed });
      onCategoryChange?.(trimmed);
    } catch { /* ignore */ }
    setEditingCategory(false);
  };
  const pipelineSteps = pipeline
    ? PIPELINE_STEPS.map(s => ({
        label: s.label,
        done: !!(pipeline[s.key] as { completedAt?: string } | undefined)?.completedAt,
      }))
    : [];
  const completedCount = pipelineSteps.filter(s => s.done).length;

  return (
    <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', overflow: 'hidden', maxWidth: '100%' }}>
      {/* Left: startup identity */}
      <div style={{ flex: '1 1 auto', minWidth: 140 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
          <h1 className="text-xl sm:text-3xl font-bold text-white" style={{ wordBreak: 'break-word' }}>{startup.name}</h1>
          <span className={`badge badge-${startup.stage}`}>{STAGE_LABELS[startup.stage] ?? startup.stage}</span>
          {startup.fromPipelineId && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
              background: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.25)',
            }}><Zap size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> From Pipeline</span>
          )}
        </div>
        {startup.description && (
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 10, maxWidth: 520, lineHeight: 1.5 }}>
            {startup.description}
          </p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {startup.problemStatement && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 12,
              background: '#ef444415', color: '#ef4444', border: '1px solid #ef444430',
            }}>
              Problem: {startup.problemStatement.slice(0, 55)}{startup.problemStatement.length > 55 ? '…' : ''}
            </span>
          )}
          {startup.targetSegment && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 12,
              background: '#7c6ff715', color: '#7c6ff7', border: '1px solid #7c6ff730',
            }}>
              {startup.targetSegment}
            </span>
          )}
          {startup.uniqueValueProposition && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 12,
              background: 'rgba(0,212,255,0.08)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.2)',
            }}>
              UVP: {startup.uniqueValueProposition.slice(0, 40)}{startup.uniqueValueProposition.length > 40 ? '…' : ''}
            </span>
          )}
          {/* Editable Industry/Category tag */}
          {editingCategory ? (
            <input
              autoFocus
              value={categoryValue}
              onChange={e => setCategoryValue(e.target.value)}
              onBlur={() => saveCategory(categoryValue)}
              onKeyDown={e => { if (e.key === 'Enter') saveCategory(categoryValue); if (e.key === 'Escape') setEditingCategory(false); }}
              placeholder="e.g. vintage clothing, farm equipment"
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12, width: '100%', maxWidth: 200,
                background: 'rgba(249,115,22,0.08)', color: '#f97316', border: '1px solid rgba(249,115,22,0.4)',
                outline: 'none',
              }}
            />
          ) : (
            <button
              onClick={() => { setCategoryValue(category ?? ''); setEditingCategory(true); }}
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12, cursor: 'pointer',
                background: isGenericCategory ? 'rgba(239,68,68,0.08)' : category ? 'rgba(249,115,22,0.08)' : 'rgba(255,255,255,0.04)',
                color: isGenericCategory ? '#ef4444' : category ? '#f97316' : '#6b7280',
                border: `1px solid ${isGenericCategory ? 'rgba(239,68,68,0.3)' : category ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.1)'}`,
              }}
              title="Click to set your industry/category for better AI analysis"
            >
              {isGenericCategory ? `"${category}" is too generic — click to specify` : category ? `Industry: ${category}` : '+ Set Industry/Category'}
            </button>
          )}
        </div>
        <p style={{ fontSize: 12, color: '#4b5563' }}>Last updated {timeAgo(startup.updatedAt)}</p>
      </div>

      {/* Right: pipeline completion (only when linked) */}
      {pipeline && pipelineSteps.length > 0 && (
        <div style={{ minWidth: 0, width: 'auto', maxWidth: 200, flexShrink: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Pipeline
            </span>
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: completedCount === pipelineSteps.length ? '#22c55e' : '#f59e0b',
            }}>
              {completedCount}/{pipelineSteps.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {pipelineSteps.map((step) => (
              <div key={step.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                  background: step.done ? '#22c55e' : 'transparent',
                  border: `2px solid ${step.done ? '#22c55e' : '#2a2a3e'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {step.done && <Check size={8} style={{ color: '#030712' }} />}
                </div>
                <span style={{ fontSize: 12, color: step.done ? '#d1d5db' : '#4b5563' }}>{step.label}</span>
              </div>
            ))}
          </div>
          <div style={{ height: 4, background: '#1e1e30', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${pipelineSteps.length > 0 ? (completedCount / pipelineSteps.length) * 100 : 0}%`,
              background: 'linear-gradient(90deg, #7c6ff7, #22c55e)',
              borderRadius: 2, transition: 'width 0.8s ease',
            }} />
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignSelf: 'flex-start', flexShrink: 1 }}>
        <button onClick={onPrint} className="btn-secondary text-sm flex items-center gap-1.5"><FileDown size={14} /> Export PDF</button>
        <button
          onClick={onRunIntelligence}
          disabled={isRunningAny}
          className="btn-primary text-sm flex items-center gap-1.5"
          style={{ whiteSpace: 'nowrap' }}
        >
          {isRunningAny ? <><Loader size={14} className="animate-spin" /> Analysing...</> : <><Sparkles size={14} /> Run Analysis</>}
        </button>
      </div>
    </div>
  );
}
